import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import { StatusPill } from '@/components/ui/status-pill';
import { FilterChip } from '@/components/ui/filter-chip';
import { DataTable, type ColumnDef } from '@/components/ui/data-table';
import { taxYearLabel } from '@/lib/utils';
import type {
  PrefillSnapshot,
  AdminModuleAgentPrefillResponse,
} from '@/api/generated/types.gen';

export const Route = createFileRoute('/admin/agent/prefill')({
  component: AgentPrefill,
});

const STATUSES = ['Ok', 'NotAvailable', 'NotAuthenticated', 'Failed'] as const;
type Status = (typeof STATUSES)[number] | 'All';

function AgentPrefill() {
  const [status, setStatus] = useState<Status>('All');

  const queryString = useMemo(() => {
    const p = new URLSearchParams({ pageNumber: '1', pageSize: '100' });
    if (status !== 'All') p.set('status', status);
    return p.toString();
  }, [status]);

  const snapshots = useQuery({
    queryKey: ['admin', 'agent', 'prefill', queryString],
    queryFn: () =>
      api<AdminModuleAgentPrefillResponse>(`/admin/agent/prefill?${queryString}`),
    retry: false,
  });
  const items: PrefillSnapshot[] = snapshots.data?.items ?? [];

  const columns = useMemo<ColumnDef<PrefillSnapshot>[]>(
    () => [
      {
        id: 'return',
        accessorFn: (s) => Number(s.taxReturnId),
        header: 'Return',
        cell: ({ row }) => {
          const s = row.original;
          return (
            <div>
              <Link
                to="/admin/returns/$id"
                params={{ id: String(s.taxReturnId) }}
                className="font-medium text-(--color-navy) underline-offset-4 hover:underline"
              >
                Return #{s.taxReturnId}
              </Link>
              {s.message && (
                <div className="mt-0.5 text-[12px] text-(--color-ink-muted)">{s.message}</div>
              )}
            </div>
          );
        },
      },
      {
        id: 'year',
        accessorFn: (s) => Number(s.year),
        header: 'Year',
        cell: ({ row }) => (
          <span className="text-(--color-ink-2) tabular-nums">FY {taxYearLabel(Number(row.original.year))}</span>
        ),
      },
      {
        id: 'status',
        accessorFn: (s) => s.status ?? 'Failed',
        header: 'Status',
        cell: ({ row }) => {
          const st = row.original.status ?? 'Failed';
          return <StatusPill tone={prefillTone(st)}>{prettyStatus(st)}</StatusPill>;
        },
      },
      {
        id: 'fetched',
        accessorFn: (s) => s.fetchedAt as string,
        header: 'Fetched',
        cell: ({ row }) => (
          <span className="font-mono text-[13px] tabular-nums text-(--color-ink-muted)">
            {new Date(row.original.fetchedAt).toLocaleString('en-AU')}
          </span>
        ),
      },
      {
        id: 'docsize',
        accessorFn: (s) => Number(s.rawDocumentLength ?? 0),
        header: 'Doc size',
        cell: ({ row }) => {
          const rawLen = Number(row.original.rawDocumentLength ?? 0);
          return (
            <span className="font-mono text-[13px] tabular-nums text-(--color-ink-muted)">
              {rawLen > 0 ? `${(rawLen / 1024).toFixed(1)} kB` : '—'}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="section-ref">Status</span>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip label="All" active={status === 'All'} onClick={() => setStatus('All')} />
          {STATUSES.map((s) => (
            <FilterChip
              key={s}
              label={prettyStatus(s)}
              active={status === s}
              onClick={() => setStatus(s)}
            />
          ))}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={snapshots.isLoading}
        loadingMessage="Loading prefill snapshots…"
        emptyMessage={snapshots.isError ? 'Could not load prefill snapshots.' : 'No snapshots match.'}
        getRowId={(s) => String(s.id)}
        initialSorting={[{ id: 'fetched', desc: true }]}
      />

      {snapshots.data && (
        <p className="text-[12px] text-(--color-ink-muted)">
          Showing {items.length} of {Number(snapshots.data.totalCount ?? 0)}.
        </p>
      )}
    </div>
  );
}

function prettyStatus(s: string): string {
  return s
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase();
}

function prefillTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'Ok':
      return 'success';
    case 'NotAuthenticated':
    case 'NotAvailable':
      return 'warning';
    case 'Failed':
      return 'danger';
    default:
      return 'neutral';
  }
}
